// File: src/pages/InvoiceViewerPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/axios";
import { Download, CheckCircle2, Loader2, ArrowLeft } from "lucide-react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Link, useLocation, useParams } from "react-router-dom";

/* ---------------- helpers ---------------- */
const toStr = (v) => (v == null ? "" : String(v));
const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

function normalizeInvoices(data) {
  const rows = Array.isArray(data)
    ? data
    : Array.isArray(data?.items)
      ? data.items
      : Array.isArray(data?.rows)
        ? data.rows
        : Array.isArray(data?.invoices)
          ? data.invoices
          : Array.isArray(data?.data)
            ? data.data
            : [];

  return rows.filter(Boolean);
}

/**
 * Build a download URL that points to the API host (not the Vercel UI host).
 * - If inv.file_url is absolute => use as-is
 * - If inv.file_url is relative => prefix with API origin
 * - Else if inv.filename exists => /uploads/invoices/<filename> on API origin
 */
function buildInvoiceDownloadHref(inv) {
  const fileUrl = toStr(inv?.file_url || inv?.fileUrl || "");
  const filename = toStr(inv?.filename || inv?.file_name || inv?.stored_filename || "");

  // API base from env (commonly includes /solo-mdsh-api)
  const rawBase = toStr(import.meta.env.VITE_API_BASE_URL || "").replace(/\/+$/, "");
  const apiOrigin = rawBase.replace(/\/solo-mdsh-api\/?$/i, "");

  const join = (base, path) => {
    const b = toStr(base).replace(/\/+$/, "");
    const p = toStr(path).startsWith("/") ? toStr(path) : `/${toStr(path)}`;
    return `${b}${p}`;
  };

  // absolute URL
  if (/^https?:\/\//i.test(fileUrl)) return fileUrl;

  // relative URL coming from backend
  if (fileUrl) {
    // if backend returns "/uploads/..." this MUST hit API origin
    if (fileUrl.startsWith("/")) return apiOrigin ? join(apiOrigin, fileUrl) : fileUrl;
    // if backend returns "uploads/..." make it absolute
    return apiOrigin ? join(apiOrigin, `/${fileUrl}`) : `/${fileUrl}`;
  }

  // filename fallback
  if (filename) {
    const rel = `/uploads/invoices/${encodeURIComponent(filename)}`;
    return apiOrigin ? join(apiOrigin, rel) : rel;
  }

  return null;
}

/* ---------------- Page ---------------- */
export default function InvoiceViewerPage() {
  const { invoiceId } = useParams(); // route is /invoice/view/:invoiceId
  const location = useLocation();

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [filterStatus, setFilterStatus] = useState("all");
  const [filterVendor, setFilterVendor] = useState("");

  const fetchInvoices = async () => {
    setLoading(true);
    setErr("");

    // IMPORTANT: do NOT hardcode "/solo-mdsh-api" here; axios base already handles it in your app.
    const paths = [
      "/invoices",                 // most common
      "/procurement/invoices",     // if mounted under procurement
      "/invoices/history",         // fallback
      "/procurement/invoices/history",
    ];

    let lastError = null;

    for (const p of paths) {
      try {
        const res = await api.get(p);
        const rows = normalizeInvoices(res?.data);
        setInvoices(rows);
        lastError = null;
        break;
      } catch (e) {
        lastError = e;
        if (e?.response?.status === 404) continue;
        break;
      }
    }

    if (lastError) {
      setInvoices([]);
      setErr(lastError?.response?.data?.error || lastError?.message || "Failed to fetch invoices.");
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const markAsPaid = async (id) => {
    if (!id) return;

    // Backend you shared uses: POST /invoices/:invoiceId/pay
    const paths = [
      `/invoices/${id}/pay`,
      `/procurement/invoices/${id}/pay`,
    ];

    try {
      let ok = false;
      for (const p of paths) {
        try {
          await api.post(p);
          ok = true;
          break;
        } catch (e) {
          if (e?.response?.status === 404) continue;
          throw e;
        }
      }
      if (!ok) throw new Error("No working pay endpoint found.");

      setInvoices((prev) =>
        prev.map((inv) =>
          (inv.invoice_id ?? inv.id) === id
            ? { ...inv, payment_status: "paid", paid_at: new Date().toISOString() }
            : inv
        )
      );
    } catch (e) {
      console.error("❌ Failed to mark invoice as paid:", e?.response?.data || e?.message);
    }
  };

  const filtered = useMemo(() => {
    return invoices.filter((inv) => {
      const status = toStr(inv.payment_status || inv.status || "").toLowerCase();
      const vendor = toStr(inv.vendor_name || inv.vendor || "").toLowerCase();

      const matchesStatus =
        filterStatus === "all" ? true : status === filterStatus;

      const matchesVendor =
        !filterVendor ? true : vendor.includes(toStr(filterVendor).toLowerCase());

      return matchesStatus && matchesVendor;
    });
  }, [invoices, filterStatus, filterVendor]);

  // If route includes an invoiceId, show just that invoice (still using same fetched data)
  const singleInvoice = useMemo(() => {
    if (!invoiceId) return null;
    const idNum = Number(invoiceId);
    return (
      invoices.find((x) => Number(x?.invoice_id ?? x?.id) === idNum) ||
      invoices.find((x) => toStr(x?.invoice_number) === toStr(invoiceId))
    );
  }, [invoiceId, invoices]);

  const listToRender = invoiceId ? (singleInvoice ? [singleInvoice] : []) : filtered;

  return (
    <div className="min-h-screen p-8 text-white bg-[#0b0f19]">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold">📁 Invoice Manager</h1>
          <p className="text-sm text-white/60 mt-1">
            {invoiceId ? "Viewing invoice" : "Browse, filter, download and mark as paid."}
          </p>
        </div>

        <div className="flex items-center gap-2">
          {invoiceId && (
            <Link
              to="/invoices"
              className="px-3 py-2 rounded bg-white/10 hover:bg-white/15 border border-white/10 text-sm inline-flex items-center gap-2"
            >
              <ArrowLeft size={16} />
              Back to list
            </Link>
          )}
          <button
            type="button"
            onClick={fetchInvoices}
            className="px-3 py-2 rounded bg-white/10 hover:bg-white/15 border border-white/10 text-sm"
          >
            Refresh
          </button>
        </div>
      </div>

      {!invoiceId && (
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="bg-[#10162a] border border-white/10 px-4 py-2 rounded"
          >
            <option value="all">All Statuses</option>
            <option value="unpaid">Unpaid</option>
            <option value="paid">Paid</option>
          </select>

          <input
            type="text"
            value={filterVendor}
            onChange={(e) => setFilterVendor(e.target.value)}
            placeholder="Search Vendor..."
            className="bg-[#10162a] border border-white/10 px-4 py-2 rounded"
          />
        </div>
      )}

      {loading ? (
        <div className="text-blue-300 flex items-center gap-2">
          <Loader2 className="animate-spin" /> Loading invoices...
        </div>
      ) : err ? (
        <div className="text-sm text-red-300 border border-red-500/30 bg-red-900/20 rounded-xl p-4">
          {err}
        </div>
      ) : invoiceId && !singleInvoice ? (
        <div className="text-yellow-300">
          Invoice not found in current list (ID: {invoiceId}).{" "}
          <button className="underline" onClick={fetchInvoices}>Try refresh</button>.
        </div>
      ) : listToRender.length === 0 ? (
        <p className="text-yellow-400">No invoices found.</p>
      ) : (
        <div className="grid gap-4">
          {listToRender.map((inv) => {
            const id = inv.invoice_id ?? inv.id;
            const due = inv.due_date ? new Date(inv.due_date) : null;
            const amount = toNum(inv.amount_due ?? inv.amount ?? inv.total ?? 0);

            const status = toStr(inv.payment_status || inv.status || "unpaid").toLowerCase();
            const downloadHref = buildInvoiceDownloadHref(inv);

            return (
              <Card
                key={toStr(id) || toStr(inv.invoice_number)}
                className="bg-[#0f162b] border border-white/10 p-4 rounded-xl"
              >
                <div className="flex justify-between items-center gap-4">
                  <div>
                    <p className="font-bold text-white">
                      Vendor: {inv.vendor_name || inv.vendor || "—"}
                    </p>
                    <p className="text-sm text-white/60">
                      Due: {due ? format(due, "dd MMM yyyy") : "—"}
                    </p>
                    <p className="text-sm text-white/60">
                      Amount: £{amount.toFixed(2)}
                    </p>
                    <p className={`text-sm font-semibold ${status === "paid" ? "text-green-400" : "text-red-400"}`}>
                      {status.toUpperCase()}
                    </p>

                    {!invoiceId && (id != null) && (
                      <div className="mt-2">
                        <Link to={`/invoice/view/${id}`} className="text-cyan-300 hover:underline text-sm">
                          Open invoice →
                        </Link>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-3 items-center">
                    {downloadHref && (
                      <a
                        href={downloadHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-400 hover:text-white"
                        title="Download / Open invoice"
                      >
                        <Download size={18} />
                      </a>
                    )}

                    {status !== "paid" && id != null && (
                      <Button
                        onClick={() => markAsPaid(id)}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <CheckCircle2 size={16} className="mr-1" /> Mark Paid
                      </Button>
                    )}
                  </div>
                </div>

                {inv.notes && (
                  <p className="text-sm text-yellow-200 italic mt-3">
                    Note: {inv.notes}
                  </p>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}